import {ReactElement, useState} from 'react'
import {WithNamespaces, withNamespaces} from 'react-i18next'
import {Grid} from '@material-ui/core'
import useDebouncedValue from '../../../../../shared/hooks/useDebouncedValue'
import {useQueryApiV2} from '../../../../../shared/hooks/use-query-api-v2'
import {usePagination} from '../../../../../shared/hooks/usePagination'
import {useSort} from '../../../../../shared/hooks/useSort'
import {useFilters} from '../../../../../shared/hooks/useFilters'
import {useProvider} from '../../../Forex/hooks/useProvider'
import {Account} from '../../../../../graphql/types'
import Loading from '../../../../Common/Loading'
import {getMetadataFromAccount} from '../../../AccountCard/utils'
import {ErrorTableLayout} from '../../../../Common/ErrorTableLayout'
import {Pagination} from '../../../../Common/Pagination'
import {FilterFields} from './components/FilterFields'
import {TableContent} from './components/TableContent'
import {ApiResponse, FilterValues, SortableFields} from './types'
import {fromFilters} from './utils'
import {
  downloadPerformanceFeeHistoryXlsx,
  fetchAllPerformanceFeeDeposits,
} from './exportPerformanceFeeHistory'

type TransactionsHistoryProps = {
  account: Account
} & WithNamespaces

function TransactionsHistoryBase(props: TransactionsHistoryProps): ReactElement {
  const {account, lng} = props
  const [exporting, setExporting] = useState(false)

  const paginationApi = usePagination({limit: 10})
  const sortApi = useSort<SortableFields>({orderBy: 'completedAt', orderDirection: 'DESC'})
  const filtersApi = useFilters<FilterValues>({
    defaultValue: {search: '', completedAfter: null, completedBefore: null},
    onChange: () => paginationApi.reset(),
    onReset: () => paginationApi.reset
  })
  const debouncedFilters = useDebouncedValue(filtersApi.value, 300)

  const brokereeProviderAccountId = getMetadataFromAccount(account).brokereeProviderAccountId as number
  const {provider, loading: isProviderLoading} = useProvider(brokereeProviderAccountId)

  const [performanceFee, {loading: isPerformanceFeeLoading}] = useQueryApiV2<ApiResponse>({
    path: `/dashboard/accounts/${account.id}/deposits/performance-fee`,
    params: {...paginationApi.value, ...sortApi.value, ...fromFilters(debouncedFilters)},
  })

  const handleExport = async () => {
    if (account.id == null || provider?.offer?.name == null || exporting) return

    setExporting(true)
    try {
      const deposits = await fetchAllPerformanceFeeDeposits({
        accountId: account.id,
        filters: debouncedFilters,
        sortApi,
      })
      downloadPerformanceFeeHistoryXlsx({
        deposits,
        offerName: provider.offer.name,
        locale: lng ?? 'en',
        fileName: `performance-fee-history-${account.id}`,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setExporting(false)
    }
  }

  if (isProviderLoading || isPerformanceFeeLoading) return <Loading/>
  if (performanceFee == null || provider?.offer == null) return <ErrorTableLayout />

  const {deposits, totalCount} = performanceFee

  paginationApi.sanitizeWith(totalCount)

  return (
    <>
      <FilterFields
        filtersApi={filtersApi}
        onExport={handleExport}
        exportDisabled={totalCount === 0}
        exporting={exporting}
      />
      <TableContent
        offerName={provider.offer.name}
        deposits={deposits}
        sortApi={sortApi}
      />
      <Grid item container xs={12}>
        <Pagination
          paginationApi={paginationApi}
          totalCount={totalCount}
        />
      </Grid>
    </>
  )
}

export const TransactionsHistory = withNamespaces()(TransactionsHistoryBase)
